import { useEffect, useState, useMemo } from 'react';
import { fetchJson } from './fetchJson';

export default function useCurrentUser() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchJson('/api/auth/me');
        if (!mounted) return;
        setUser(data.user);
        setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
      } catch (err) {
        if (!mounted) return;
        setError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const can = useMemo(() => {
    const has = (perm) => permissions.includes(perm);

    // /api/auth/me returns PAGE PATHS ('/user-list', '/user-form', …), never the
    // uppercase capability strings below — those are a legacy vocabulary nothing
    // on the server emits. Gating on them alone meant every user-management
    // capability read false for everybody, which is why the user directory
    // showed no Change Role, Reset Password, Resend Invite or Edit action to
    // anyone at all.
    //
    // Page access is the right authority anyway: it is exactly what the server
    // guards these endpoints with, so the buttons now appear precisely when the
    // call behind them would be allowed. The cap strings are kept as an OR so
    // any caller still speaking that vocabulary keeps working.
    const users = has('/user-list');
    const userForm = has('/user-form');

    // Common user-management capabilities
    return {
      manageUsers: users || userForm || has('MANAGE_USERS') || has('TEACHER_MANAGEMENT') || has('STUDENT_MANAGEMENT'),
      assignRoles: users || userForm || has('MANAGE_ROLES') || has('MANAGE_USERS'),
      resetPasswords: users || has('MANAGE_USERS') || has('SCHOOL_SETTINGS'),
      inviteUsers: users || has('MANAGE_USERS') || has('COMMUNICATION_MANAGEMENT'),
      deleteUsers: users || has('MANAGE_USERS'),
      
      // Staff management permissions
      manageStaff: has('MANAGE_STAFF') || has('STAFF_MANAGEMENT') || has('MANAGE_USERS'),
      
      // Payroll and salary management permissions
      managePayroll: has('MANAGE_PAYROLL') || has('FINANCE_MANAGEMENT') || has('SALARY_MANAGEMENT'),
      viewPayroll: has('VIEW_PAYROLL') || has('FINANCE_VIEW') || has('MANAGE_PAYROLL'),
      exportPayroll: has('EXPORT_PAYROLL') || has('FINANCE_EXPORT') || has('MANAGE_PAYROLL'),
      
      // Finance permissions
      manageFinance: has('FINANCE_MANAGEMENT') || has('MANAGE_FINANCE'),
      viewFinance: has('FINANCE_VIEW') || has('MANAGE_FINANCE'),
      
      // Phase management permissions
      switchPhases: has('SWITCH_PHASES') || has('MANAGE_PHASES'),
      manageSchool: has('SCHOOL_ADMIN') || has('MANAGE_SCHOOL'),
      
      raw: permissions,
    };
  }, [permissions]);

  return { user, permissions, can, loading, error };
}
